import { useAuthActions } from "@convex-dev/auth/react";
import { convexQuery } from "@convex-dev/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useQuery } from "@tanstack/react-query";
import { makeRedirectUri } from "expo-auth-session";
import { Redirect, useLocalSearchParams, useRouter } from "expo-router";
import React, { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Pressable, ScrollView, Text, View } from "react-native";
import Toast from "react-native-toast-message";
import { z } from "zod";
import EyeOffIcon from "@/assets/svgs/eye-off-icon";
import EyeOpenIcon from "@/assets/svgs/eye-open-icon";
// import GoogleIcon from "@/assets/svgs/google-icon";
import Loader from "@/elements/components/shared/loader";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { AppBar } from "@/elements/components/ui/app-bar";
import { Button } from "@/elements/components/ui/button";
import { Input } from "@/elements/components/ui/input";
import { handleAuthError } from "@/elements/components/utils/auth-error-handler";
import { auth } from "@/elements/components/utils/convex-api";
import { phoneNumberSchema } from "@/elements/components/utils/phoneNumberSchema";

const createSignUpSchema = (persona?: "parent" | "tutor" | "student") =>
  z
    .object({
      firstName: z.string().min(1, "First name is required"),
      lastName: z.string().min(1, "Last name is required"),
      phone: persona === "student" ? z.string().optional() : phoneNumberSchema,
      email: z.string().email("Please enter a valid email address"),
      password: z
        .string()
        .min(8, "Password must be at least 8 characters long")
        .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
        .refine(
          (password) =>
            /\d/.test(password) || /[!@#$%^&*(),.?":{}|<>]/.test(password),
          "Password must contain at least one number or symbol",
        ),
      confirmPassword: z.string().min(1, "Please confirm your password"),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: "Passwords do not match",
      path: ["confirmPassword"],
    });

type SignUpSchemaType = z.infer<ReturnType<typeof createSignUpSchema>>;

function SignUpScreen() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);

  const { signIn: authorize } = useAuthActions();

  const { persona, email } = useLocalSearchParams<{
    persona?: "parent" | "tutor" | "student";
    email?: string;
  }>();

  const schema = useMemo(() => createSignUpSchema(persona), [persona]);

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      email: email || "",
      password: "",
      confirmPassword: "",
    },
    mode: "onChange",
    resolver: zodResolver(schema),
  });

  // const redirectTo = useMemo(
  //   () =>
  //     makeRedirectUri({
  //       scheme: "teachloop",
  //       path: "sign-up",
  //       queryParams: {
  //         persona,
  //       },
  //     }),
  //   [persona],
  // );

  const { data: isAuthenticated = false, isSuccess } = useQuery(
    convexQuery(auth.isAuthenticated, {}),
  );

  if (!persona) {
    return <Redirect href={"/(auth)/sign-up-options"} />;
  }

  if (!isSuccess) {
    return <Loader />;
  }

  if (isAuthenticated) {
    return <Redirect href={"/(app)/home"} />;
  }

  const onSubmit = async (data: SignUpSchemaType) => {
    try {
      await AsyncStorage.setItem("persona", persona);
      if (data.phone) await AsyncStorage.setItem("phone", data.phone);

      const success = await authorize("password", {
        email: data.email.toLowerCase().trim(),
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phone ?? "",
        password: data.password,
        context: persona,
        flow: "signUp",
        redirectTo: makeRedirectUri(),
      });

      if (success?.signingIn !== true) {
        router.push({
          pathname: "/(auth)/verify-email",
          params: { email: data.email.toLowerCase().trim(), persona },
        });
      }
    } catch (error) {
      const errorResult = handleAuthError(error);
      console.log(errorResult);
      Toast.show({
        type: "error",
        text1: errorResult.message,
        text1Style: {
          fontFamily: "Geologica_400Regular",
          fontSize: 10,
          color: "#000000",
        },
      });
    }
  };

  // const onGoogleSignUp = async () => {
  //   await AsyncStorage.setItem("persona", persona);
  //   await authorize("google", {
  //     redirectTo: redirectTo,
  //   });
  // };

  // const onAppleSignUp = async () => {
  //   await AsyncStorage.setItem("persona", persona);
  //   await authorize("apple", {
  //     redirectTo: redirectTo,
  //   });
  // };

  return (
    <View className="flex-1 bg-teachLoop-creamLight">
      <AppBar>
        <AppBar.Icon
          onPress={() =>
            router.canGoBack()
              ? router.back()
              : router.replace("/(auth)/sign-up-options")
          }
        />
        <AppBar.Title>Back to signup</AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-8" />
            <View className="gap-1">
              <Text className="font-zilla-slab-medium leading-[1.2] font-medium text-[28px] text-black">
                Tell us about yourself
              </Text>
              <Text className="font-geologica-regular font-normal text-[20px] text-black leading-[1.5] ">
                {`Let's start with the basics.`}
              </Text>
            </View>

            <View className="h-8" />

            <View className="gap-4">
              <Controller
                control={control}
                name="firstName"
                render={({ field, fieldState }) => (
                  <Input
                    size="md"
                    placeholder="First name"
                    {...field}
                    error={!!fieldState.error?.message}
                  />
                )}
              />

              <Controller
                control={control}
                name="lastName"
                render={({ field, fieldState }) => (
                  <Input
                    size="md"
                    placeholder="Last name"
                    {...field}
                    error={!!fieldState.error?.message}
                  />
                )}
              />

              {persona !== "student" && (
                <View className="gap-2">
                  <Controller
                    control={control}
                    name="phone"
                    render={({ field, fieldState }) => (
                      <View className="gap-2">
                        <Input
                          size="md"
                          placeholder="Mobile number"
                          {...field}
                          error={!!fieldState.error?.message}
                          keyboardType="phone-pad"
                        />

                        {!!fieldState.error?.message && (
                          <Input.Message>
                            {fieldState.error?.message}
                          </Input.Message>
                        )}
                      </View>
                    )}
                  />
                </View>
              )}

              <Controller
                control={control}
                name="email"
                render={({ field, fieldState }) => (
                  <Input
                    size="md"
                    placeholder="Email"
                    {...field}
                    error={!!fieldState.error?.message}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    disabled={persona === "student"}
                  />
                )}
              />

              <Controller
                control={control}
                name="password"
                render={({ field, fieldState }) => (
                  <View className="gap-2">
                    <View>
                      <Input
                        size="md"
                        placeholder="Password"
                        {...field}
                        secureTextEntry={!showPassword}
                        error={!!fieldState.error?.message}
                      />
                      <Pressable
                        onPress={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2"
                      >
                        {showPassword ? (
                          <EyeOpenIcon
                            className="size-6"
                            pathClassName="stroke-gray-400"
                          />
                        ) : (
                          <EyeOffIcon
                            className="size-6"
                            pathClassName="stroke-gray-400"
                          />
                        )}
                      </Pressable>
                    </View>
                    {(!!fieldState.error?.message ||
                      field.value.length === 0) && (
                      <Input.Message>
                        Password must be a minimum 8 characters. {"\n"}Include
                        one capital letter and one number or symbol.
                      </Input.Message>
                    )}
                  </View>
                )}
              />
              <Controller
                control={control}
                name="confirmPassword"
                render={({ field, fieldState }) => (
                  <Input
                    size="md"
                    placeholder="Confirm password"
                    {...field}
                    secureTextEntry
                    error={!!fieldState.error?.message}
                  />
                )}
              />
            </View>
            <View className="h-[24]" />
            <Button
              className="w-full"
              size="md"
              intent="primary"
              onPress={handleSubmit(onSubmit)}
              disabled={!isValid}
            >
              <Button.Text size="md">Next</Button.Text>
            </Button>

            <View className="h-6" />
            {/* <View className="flex-row items-center gap-4 px-[27px]">
              <View className="flex-1 h-[1px] bg-gray-300" />
              <Text className="text-black text-base font-geologica-medium font-medium">
                Or
              </Text>
              <View className="flex-1 h-[1px] bg-gray-300" />
            </View>
            <View className="h-6" /> */}
            {/* <View className="gap-4">
              <Button
                className="w-full"
                size="md"
                intent="primary"
                outline
                onPress={onGoogleSignUp}
                icon
              >
                <Button.Icon
                  as={GoogleIcon}
                  intent="primary"
                  className="size-5"
                />
                <Button.Text size="md" intent="primary">
                  With Google
                </Button.Text>
              </Button>
            </View>
            <View className="h-8" /> */}
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}
export default SignUpScreen;
